import { createContext, useEffect, useState, useContext } from "react";
import { useLocation } from "react-router-dom";
import useQueryParams from "../hooks/use-queryParams";
import { getDatesAPI } from "../api/Auth/DatesAPI";
import { AuthContext } from "../context/AuthProvider";

// Initial Data to import
import {
  isYearValues,
  isApplicationDates,
  isDailyMonthlyRadioItemOptions,
} from "../constants/initialDataConfig";

const DateContext = createContext({});

// Provides access to token across whole application
export const DateProvider = ({ children }) => {
  // Get the query parameters from the URL for the setting the active tab
  const location = useLocation();
  const query = useQueryParams();
  const activeTabURL = query.get("activeTab") || 0;

  // State: Initialize State - Full Application
  const [dateIsLoading, setDateIsLoading] = useState(true);

  // State: Initialize State - Date Values
  const [dateValues, setDateValues] = useState([]);

  // State: Year Values - Date Values
  const [yearValues, setYearValues] = useState(isYearValues);

  // State: Month Values - Date Values
  const [monthValues, setMonthValues] = useState({});

  // State: Quarter Values - Date Values
  const [quarterValues, setQuarterValues] = useState({});

  // State: Set Application Dates
  const [applicationDates, setApplicationDates] = useState(isApplicationDates);

  // State: Set Active Tabs - Application
  const [activeTab, setActiveTab] = useState({
    "/vehicle-performance":
      location?.pathname === "/vehicle-performance" ? activeTabURL : 0,
    "/settings": location?.pathname === "/settings" ? activeTabURL : 0,
    "/emissions": location?.pathname === "/emissions" ? activeTabURL : 0,
    // "/advanced-analytics":
    //   location?.pathname === "/advanced-analytics" ? activeTabURL : 0,
  });

  const [selectedChartRadioItemType, setSelectedChartRadioItemType] = useState(
    isDailyMonthlyRadioItemOptions
  );

  // Const: Get Token & Make Sure Available For API Call
  const token = localStorage.getItem("greenToken");

  const { carrierName } = useContext(AuthContext); // Stored Email For Help Page
  const isSCSCustomer = ["Customer1", "Customer2", "Customer3"]?.includes(
    carrierName
  );

  // UE: Get Dates For the Whole Application
  useEffect(() => {
    if (dateIsLoading && token) {
      const promises = [
        getDatesAPI(
          setDateValues,
          setYearValues,
          setSelectedChartRadioItemType,
          setMonthValues,
          setQuarterValues,
          isSCSCustomer
        ),
      ];
      Promise.all(promises).then(() => {
        setDateIsLoading(false);
      });
    }
  }, [dateIsLoading, token]);

  // Initialize all date components to be the most recent date
  useEffect(() => {
    if (dateValues?.length > 0) {
      // Determine Which Date to Show
      let todaysDate = new Date();
      const now = new Date();
      const utcNow = new Date(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        now.getUTCHours(),
        now.getUTCMinutes(),
        now.getUTCSeconds(),
        now.getUTCMilliseconds()
      );

      todaysDate = utcNow;

      const showCurrentMonth =
        parseInt(
          todaysDate.toISOString().split("T")[0]?.split("-")?.slice(-1)?.[0]
        ) > 2;

      const showIndustryCarbon =
        parseInt(
          todaysDate.toISOString().split("T")[0]?.split("-")?.slice(-1)?.[0]
        ) > 15;

      const initialDate = dateValues?.slice(-1)?.[0]?.value;

      const datesDict = Object?.keys(isApplicationDates)?.reduce(
        (acc, curr) => {
          acc[curr] = initialDate;
          return acc;
        },
        {}
      );

      setApplicationDates({
        ...datesDict,
        industryCarbonRating: isSCSCustomer
          ? initialDate
          : !showCurrentMonth
          ? initialDate
          : showIndustryCarbon
          ? initialDate
          : dateValues?.slice(-2)?.[0]?.value,
        emissionReductionTarget: initialDate.slice(0, 4), // Only the year is needed
        lifecycleChartYearly: initialDate.slice(0, 4),
      });
    }
  }, [dateValues]);

  const value = {
    dateIsLoading,
    dateValues,
    setDateValues,
    applicationDates,
    setApplicationDates,
    yearValues,
    activeTab,
    setActiveTab,
    selectedChartRadioItemType,
    setSelectedChartRadioItemType,
    monthValues,
    quarterValues,
  };

  return <DateContext.Provider value={value}>{children}</DateContext.Provider>;
};

export default DateContext;
